import React, {useCallback, useEffect, useState} from "react";
import api from "@/api/axiosConfig";
import useDebounce from "@/hooks/useDebounce";
import {SearchInput} from "@/components/search-input";

type IncidentType = {
    id: string;
    categoryID: string;
    name: string;
    severity: string;
}

type IncidentCategory = {
    id: string;
    name: string;
    types: IncidentType[];
}

type CategorySelectorProps = {
    selectedTypeIDs: string[];
    onSelectedTypeIDsChange: (typeIDs: string[]) => void;
}

export function CategorySelector({selectedTypeIDs, onSelectedTypeIDsChange}: CategorySelectorProps) {
    const [categories, setCategories] = useState<IncidentCategory[]>([]);
    const [filteredCategories, setFilteredCategories] = useState<IncidentCategory[]>([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [debouncedSearch, setDebouncedSearch] = useDebounce(300, "");

    // Update debounced search when search changes
    useEffect(() => {
        setDebouncedSearch(search);
    }, [search, setDebouncedSearch]);

    // Fetch categories on mount
    useEffect(() => {
        setLoading(true);
        api.get<IncidentCategory[]>("incident-types")
            .then((response) => {
                setCategories(response.data);
                setFilteredCategories(response.data);
            })
            .catch((error) => {
                console.error("Failed to load incident types:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // Filter categories based on search
    useEffect(() => {
        if (!debouncedSearch) {
            setFilteredCategories(categories);
            return;
        }

        const searchLower = debouncedSearch.toLowerCase();
        const filtered = categories
            .map((category) => {
                // Filter types that match search
                const matchingTypes = category.types.filter((type) =>
                    type.name.toLowerCase().includes(searchLower) ||
                    type.severity.toLowerCase().includes(searchLower) ||
                    category.name.toLowerCase().includes(searchLower)
                );

                // Include category if name matches OR has matching types
                if (
                    category.name.toLowerCase().includes(searchLower) ||
                    matchingTypes.length > 0
                ) {
                    return {
                        ...category,
                        types: matchingTypes.length > 0 ? matchingTypes : category.types,
                    };
                }

                return null;
            })
            .filter((c): c is IncidentCategory => c !== null);

        setFilteredCategories(filtered);
    }, [debouncedSearch, categories]);

    const handleCategoryToggle = useCallback((category: IncidentCategory) => {
        const categoryTypeIDs = category.types.map((t) => t.id);
        const allSelected = categoryTypeIDs.every((id) => selectedTypeIDs.includes(id));

        if (allSelected) {
            // Deselect all types in this category
            onSelectedTypeIDsChange(selectedTypeIDs.filter((id) => !categoryTypeIDs.includes(id)));
        } else {
            // Select all types in this category
            const newSelection = [...selectedTypeIDs];
            categoryTypeIDs.forEach((id) => {
                if (!newSelection.includes(id)) {
                    newSelection.push(id);
                }
            });
            onSelectedTypeIDsChange(newSelection);
        }
    }, [selectedTypeIDs, onSelectedTypeIDsChange]);

    const handleTypeToggle = useCallback((typeID: string) => {
        if (selectedTypeIDs.includes(typeID)) {
            onSelectedTypeIDsChange(selectedTypeIDs.filter((id) => id !== typeID));
        } else {
            onSelectedTypeIDsChange([...selectedTypeIDs, typeID]);
        }
    }, [selectedTypeIDs, onSelectedTypeIDsChange]);

    const isCategorySelected = useCallback((category: IncidentCategory) => {
        const categoryTypeIDs = category.types.map((t) => t.id);
        return categoryTypeIDs.length > 0 && categoryTypeIDs.every((id) => selectedTypeIDs.includes(id));
    }, [selectedTypeIDs]);

    const isCategoryPartiallySelected = useCallback((category: IncidentCategory) => {
        const categoryTypeIDs = category.types.map((t) => t.id);
        const selectedCount = categoryTypeIDs.filter((id) => selectedTypeIDs.includes(id)).length;
        return selectedCount > 0 && selectedCount < categoryTypeIDs.length;
    }, [selectedTypeIDs]);

    if (loading) {
        return (
            <div className="bg-white border border-light-grey rounded p-4">
                <div className="text-grey">Loading categories...</div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded">
            {/* Search bar */}
            <div className="mb-2">
                <SearchInput initialValue={search} onChange={setSearch} />
            </div>


            {/* Categories and types list */}
            <div className="max-h-96 overflow-y-auto">
                {filteredCategories.length === 0 ? (
                    <div className="p-4 text-grey">No categories found</div>
                ) : (
                    filteredCategories.map((category) => (
                        <div key={category.id} className="border-b border-l border-r border-light-light-grey last:border-b-0">
                            {/* Category header */}
                            <div
                                className="flex items-center gap-3 p-3 bg-light-light-grey hover:bg-light-grey cursor-pointer"
                                onClick={() => handleCategoryToggle(category)}
                            >
                                <input
                                    type="checkbox"
                                    checked={isCategorySelected(category)}
                                    ref={(el) => {
                                        if (el) {
                                            el.indeterminate = isCategoryPartiallySelected(category);
                                        }
                                    }}// Handled by div onClick
                                    className="w-4 h-4 accent-blue cursor-pointer pointer-events-none"
                                />
                                <span className="font-semibold">{category.name}</span>
                                <span className="text-sm text-grey ml-auto">
                                    {category.types.length} {category.types.length === 1 ? "type" : "types"}
                                </span>
                            </div>

                            {/* Types list */}
                            <div className="bg-white">
                                {category.types.map((type) => (
                                    <div
                                        key={type.id}
                                        className="flex items-center gap-3 p-3 pl-10 hover:bg-light-light-grey cursor-pointer border-b border-light-light-grey last:border-b-0"
                                        onClick={() => handleTypeToggle(type.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedTypeIDs.includes(type.id)}
                                            className="w-4 h-4 accent-blue cursor-pointer pointer-events-none"
                                        />
                                        <span>{type.name}</span>
                                        {/*
                                        <span
                                            className={`ml-auto text-xs px-2 py-1 rounded w-16 text-center ${
                                                type.severity === "high"
                                                    ? "bg-dark-red text-white"
                                                    : type.severity === "medium"
                                                    ? "bg-dark-orange text-white"
                                                    : "bg-green text-white"
                                            }`}
                                        >
                                            {type.severity}
                                        </span>
                                        */}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Selection summary */}
            <div className="p-3 bg-light-light-grey">
                <span className="text-sm text-grey">
                    {selectedTypeIDs.length} {selectedTypeIDs.length === 1 ? "type" : "types"} selected
                </span>
            </div>
        </div>
    );
}
